import type {
    IsoAndUnixTimestamp} from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import type { TimeRange } from "@@/shared/date-time/time-range";
import type { Except } from "type-fest";
import { z } from "zod";
import type {
    FeeSourceNotificationsSmsConditionId} from "./conditions/fee-source-notifications-sms-condition-id";
import {
    feeSourceNotificationsSmsConditionIdZodSchema,
} from "./conditions/fee-source-notifications-sms-condition-id";
import type {
    FeeSourceNotificationsSmsCondition} from "./conditions/fee-source-notifications-sms-conditions";
import {
    feeSourceNotificationsSmsConditionZodSchema,
} from "./conditions/fee-source-notifications-sms-conditions";
import type {
    FeeSourceNotificationsSmsId} from "./fee-source-notifications-sms-id";
import {
    feeSourceNotificationsSmsIdFactory,
    feeSourceNotificationsSmsIdZodSchema,
} from "./fee-source-notifications-sms-id";
import type { NotificationStatsForSms } from "./notification-stats-sms";

/**
 * Fee source for managing fees on order data \
 * E.g. fees for orders with a special delivery type, or orders bought on a specific destination etc.
 */
type FeeSourceNotificationsSms = {
    readonly _id: FeeSourceNotificationsSmsId;
    readonly _type: "FEE_SOURCE";
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly type: "NOTIFICATIONS_SMS";
    readonly conditions: Record<
        FeeSourceNotificationsSmsConditionId,
        FeeSourceNotificationsSmsCondition
    >;
    readonly conditionOrder: FeeSourceNotificationsSmsConditionId[];
    readonly filters?: never;
    readonly filterOrder?: never;
};

type Params = Except<
    FeeSourceNotificationsSms,
    "_id" | "type" | "_type" | "_createdAt"
>;
const feeSourceNotificationsSmsFactory = (
    params: Params,
): FeeSourceNotificationsSms => {
    return {
        ...params,
        _id: feeSourceNotificationsSmsIdFactory(),
        _type: "FEE_SOURCE",
        _createdAt: nowIsoAndUnix(),
        type: "NOTIFICATIONS_SMS",
    };
};

const feeSourceNotificationsSmsZodSchema: z.ZodSchema<FeeSourceNotificationsSms> =
    z.object({
        _id: feeSourceNotificationsSmsIdZodSchema,
        _type: z.literal("FEE_SOURCE"),
        _createdAt: isoAndUnixZodSchema,
        type: z.literal("NOTIFICATIONS_SMS"),
        conditions: z.record(
            feeSourceNotificationsSmsConditionIdZodSchema,
            feeSourceNotificationsSmsConditionZodSchema,
        ) as z.ZodSchema<FeeSourceNotificationsSms["conditions"]>, // quickfix zod issue
        conditionOrder: z.array(feeSourceNotificationsSmsConditionIdZodSchema),
        filters: z.never().optional(),
        filterNotification: z.never().optional(),
    });

/**
 * Returns notification count for fee source
 */
const feeSourceNotificationsSms = (
    feeSource: FeeSourceNotificationsSms,
    _timeRange: TimeRange,
): NotificationStatsForSms | undefined => {
    const _conditions = feeSource.conditionOrder.map(
        conditionalId => feeSource.conditions[conditionalId],
    );

    // TODO: GET STATUS FOR GIVEN TIME RANGE
    // TODO: WE DONT HAVE THE STATS ANYWHERE YET THOUGH :)

    return undefined;
};

export {
    feeSourceNotificationsSms,
    feeSourceNotificationsSmsFactory,
    feeSourceNotificationsSmsZodSchema,
};
export type { FeeSourceNotificationsSms };
